import type { AuthResponse, User } from "@supabase/supabase-js";

import { supabaseClient } from "@/lib/supabase";
import { fetchClientBySub } from "@/services/entities/use-users";

export async function createNewTutorUser(
  email: string,
  password: string,
  phoneNumber: string | undefined,
  firstName: string,
  lastName: string,
): Promise<AuthResponse | undefined> {
  if (!email || !password || !firstName || !lastName) {
    throw new Error("Please fill out all fields");
  }
  if (firstName.includes(",") || lastName.includes(",")) {
    throw new Error("Please do not use commas in your name");
  }
  try {
    const { data, error } = await supabaseClient.auth.signUp({
      email: email,
      password: password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
        },
      },
    });

    if (error) {
      throw error;
    }

    if (data.user) {
      const { error: staffError } = await supabaseClient.from("staff").insert([
        {
          cognito_sub: data.user.id,
          is_tutor: true,
          email: data.user.email!,
          first_name: firstName,
          last_name: lastName,
          phone: phoneNumber,
        },
      ]);
      if (staffError) {
        // TODO: In case of failure, we should probably delete the user from auth.users
        // or have a system to clean up orphaned users.
        throw staffError;
      }
    }
    return { data, error };
  } catch (error) {
    alert(`error signing up:${error}`);
    return undefined;
  }
}

async function getCurrentUser(): Promise<User | null> {
  const { data } = await supabaseClient.auth.getSession();
  return data.session?.user ?? null;
}

/**
 * Returns true if the current signed in User is in the 'admins' cognito groups
 */
export async function isAdmin(): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) {
    return false;
  }
  const res = await supabaseClient
    .from("staff")
    .select("staff_id, is_admin, is_tutor")
    .eq("cognito_sub", user.id)
    .maybeSingle();
  return res.data?.is_admin ?? false;
}

/**
 * Returns true if the current signed in User is in the 'tutors' cognito groups
 */
export async function isTutor(): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) {
    return false;
  }
  const res = await supabaseClient
    .from("staff")
    .select("staff_id, is_tutor")
    .eq("cognito_sub", user.id)
    .maybeSingle();
  return res.data?.is_tutor ?? false;
}

/**
 * Returns true if the current signed in User is a client. They don't belong to either admin or tutor group
 */
export async function isClient(): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) {
    return false;
  }
  const res = await supabaseClient
    .from("users")
    .select("user_id")
    .eq("cognito_sub", user.id)
    .maybeSingle();
  return Boolean(res.data) ?? false;
}

/**
 * For retrieving the cognito sub token.
 * @returns The string of the current user's auth token, aka the Cognito Sub
 */
export async function getCurrentUserId(): Promise<string | null> {
  const user = await getCurrentUser();
  return user?.id ?? null;
}

/**
 * For retrieving the cognito name.
 * @returns The string of the current user's name
 */
export async function getCurrentUserName(): Promise<string | null> {
  const user = await getCurrentUser();
  if (user?.user_metadata) {
    return `${user.user_metadata.first_name} ${user.user_metadata.last_name}`;
  }
  return null;
}

/**
 * For retrieving the cognito email
 * @returns The string of the current email, aka the Cognito Sub
 */
export async function getCurrentUserEmail(): Promise<string | null> {
  const user = await getCurrentUser();
  return user?.email ?? null;
}

/**
 * For retrieving the cognito phone number
 * @returns The string of the current phone number
 */
export async function getCurrentUserPhone(): Promise<string | null> {
  const user = await getCurrentUser();
  return user?.phone ?? null;
}

/**
 * Gets the client model name from the database if the user's sub token is in the client table
 * @returns The firstName + lastName from the client model table or null
 */
export async function getCurrentUserClientName(): Promise<string | null> {
  const sub = await getCurrentUserId();
  if (!sub || sub == null) {
    return null;
  }
  const client = await fetchClientBySub(sub);
  if (client) {
    return `${client.first_name} ${client.last_name}`;
  }
  return null;
}

/**
 * Generates a random 8 character password with at least one number, one uppercase letter, and one lowercase letter, and one special character
 */
export function generateTempPassword(): string {
  let password = "";
  const possibleUpper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const possibleLower = "abcdefghijklmnopqrstuvwxyz";
  const possibleNumbers = "0123456789";
  const possibleSpecials = "!@#$%^&*()_+";

  for (let i = 0; i < 3; i++) {
    password += possibleUpper.charAt(Math.floor(Math.random() * possibleUpper.length));
    password += possibleLower.charAt(Math.floor(Math.random() * possibleLower.length));
  }
  password += possibleNumbers.charAt(Math.floor(Math.random() * possibleNumbers.length));
  password += possibleSpecials.charAt(Math.floor(Math.random() * possibleSpecials.length));
  return password;
}

export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabaseClient.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Adds a user to a cognito group
 * @param userId The id of the user to add to the group
 * @returns The status code from the API call
 * @example
 * await addToGroup("richard", "admins")
 */
export async function addUserToTutorGroup(userId: string): Promise<void> {
  // This should ideally be a call to a secure backend function
  // that verifies the current user's permissions before making such a change.
  const { error } = await supabaseClient
    .from("staff")
    .update({ is_tutor: true })
    .eq("cognito_sub", userId);

  if (error) {
    throw new Error(`Error adding user to tutor group: ${error.message}`);
  }
}
